import { Injectable } from '@angular/core';
import { HttpBackend, HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Observable, Observer, of, throwError, timer } from 'rxjs';
import { catchError, concatMap, finalize, map, retryWhen, share, switchMap } from 'rxjs/operators';

import { environment } from './../../../environments/environment';
import { CookieService } from '../services/cookie.service';

// Hoeveel keer de token-validatie een tijdelijke fout opnieuw probeert, en de
// wachttijd daartussen.
const VALIDATE_RETRIES = 2;
const VALIDATE_RETRY_DELAY_MS = 600;

// Sessie = kortlevend access token (JWT, header x-access-token) + langlevend refresh
// token dat users/auth/refresh inruilt voor een nieuw paar (zie AUTH-API.md in
// Movies-API). Alles staat in localStorage, net als in platform-web en de pro app.
// Vroeger stond het token enkel in een cookie die vanuit JS gezet werd, en zo'n
// cookie leeft in Safari maximaal 7 dagen (ITP), wat een wekelijkse herlogin
// opleverde. Niets leest die cookie server-side; ze wordt nog één keer uitgelezen
// om te migreren.
const TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const EXPIRES_AT_KEY = 'access_token_expires_at';
const LEGACY_TOKEN_COOKIE = 'access_token';
const REFRESH_LEEWAY_MS = 60 * 1000; // zoveel voor het vervallen al vernieuwen

export interface SessionTokens { token: string; refreshToken?: string; expiresIn?: number; }

@Injectable({
    providedIn: 'root'
})
export class UsersService {

    user: any;
    token: string;
    refreshToken: string | null = null;
    tokenExpiresAt: number | null = null; // ms epoch
    private refreshing: Observable<boolean> | null = null; // één refresh tegelijk
    private refreshTimer: any = null;

    // Eigen HttpClient zonder interceptors: de token-validatie moet de rauwe
    // HttpErrorResponse zien (de ErrorInterceptor vervangt die door een string)
    // en mag niet nog eens via die interceptor uitgelogd worden.
    private rawHttp: HttpClient;

    // Eén validatie tegelijk: als tien requests tegelijk een token-fout krijgen,
    // volgt daar één users/auth/current uit, niet tien.
    private revalidation: Observable<boolean> | null = null;

    constructor(private http: HttpClient, private httpBackend: HttpBackend, private cookieService: CookieService) {
        this.rawHttp = new HttpClient(httpBackend);
    }

    hasStoredToken(): boolean {
        return !!this.readStoredToken();
    }

    // Leest de sessie uit localStorage in het geheugen. Geeft het access token terug.
    private readStoredToken(): string | null {
        let token: string | null = null;
        try {
            token = localStorage.getItem(TOKEN_KEY);
            this.refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
            this.tokenExpiresAt = Number(localStorage.getItem(EXPIRES_AT_KEY) || 0) || this.decodeExpiry(token);
        } catch (error) { /* storage geblokkeerd */ }
        if (token) {
            this.token = token;
            return token;
        }

        // Sessie van voor de overstap naar localStorage: overnemen en de cookie opruimen.
        token = this.cookieService.getCookie(LEGACY_TOKEN_COOKIE);
        if (!token) return null;
        this.storeTokens({ token });
        this.cookieService.deleteCookie(LEGACY_TOKEN_COOKIE);
        return token;
    }

    private storeTokens(tokens: SessionTokens): void {
        this.token = tokens.token;
        if (tokens.refreshToken) this.refreshToken = tokens.refreshToken;
        this.tokenExpiresAt = tokens.expiresIn ? Date.now() + tokens.expiresIn * 1000 : this.decodeExpiry(tokens.token);
        try {
            localStorage.setItem(TOKEN_KEY, this.token);
            if (this.refreshToken) localStorage.setItem(REFRESH_TOKEN_KEY, this.refreshToken);
            if (this.tokenExpiresAt) localStorage.setItem(EXPIRES_AT_KEY, `${this.tokenExpiresAt}`);
        } catch (error) { /* storage geblokkeerd: sessie leeft tot de tab sluit */ }
        this.scheduleProactiveRefresh();
    }

    private decodeExpiry(token: string | null): number | null {
        if (!token) return null;
        try {
            const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
            return payload.exp ? payload.exp * 1000 : null;
        } catch (error) { return null; }
    }

    private scheduleProactiveRefresh(): void {
        if (this.refreshTimer) clearTimeout(this.refreshTimer);
        this.refreshTimer = null;
        if (!this.refreshToken || !this.tokenExpiresAt) return;
        const delay = Math.min(Math.max(this.tokenExpiresAt - Date.now() - REFRESH_LEEWAY_MS, 5000), 2 ** 31 - 1);
        this.refreshTimer = setTimeout(() => this.refreshSession().subscribe(), delay);
    }

    private accessTokenIsFresh(): boolean {
        const expiresAt = this.tokenExpiresAt || this.decodeExpiry(this.token);
        return !expiresAt || expiresAt - Date.now() > REFRESH_LEEWAY_MS;
    }

    // Ruilt het refresh token in voor een nieuw paar. true = we hebben daarna een
    // bruikbaar access token. Gelijktijdige aanroepen (interceptor, timer, guard)
    // delen één request. Een 401 van de API (token onbekend, hergebruikt, vervallen,
    // gebruiker weg) beëindigt de sessie; een netwerkfout laat alles staan.
    refreshSession(): Observable<boolean> {
        if (!this.refreshing) {
            this.refreshing = this.performRefresh().pipe(
                finalize(() => this.refreshing = null),
                share()
            );
        }
        return this.refreshing;
    }

    private performRefresh(): Observable<boolean> {
        // Een andere tab kan al vernieuwd hebben: dat paar overnemen in plaats van ons
        // weggeroteerde token te verbruiken.
        try {
            const stored = localStorage.getItem(TOKEN_KEY);
            const storedExpiry = Number(localStorage.getItem(EXPIRES_AT_KEY) || 0);
            if (stored && stored !== this.token && storedExpiry - Date.now() > REFRESH_LEEWAY_MS) {
                this.readStoredToken();
                this.scheduleProactiveRefresh();
                return of(true);
            }
        } catch (error) { /* storage geblokkeerd */ }
        if (!this.refreshToken) return of(false);

        return this.rawHttp.post<SessionTokens>(`${environment.api}users/auth/refresh`, { refreshToken: this.refreshToken })
            .pipe(
                map(tokens => {
                    if (!tokens?.token) return false;
                    this.storeTokens(tokens);
                    return true;
                }),
                catchError(error => {
                    if (error instanceof HttpErrorResponse && error.status === 401) this.logout();
                    return of(false);
                })
            );
    }

    getPaged(params): Observable<any> {
        return this.http.get<any[]>(`${environment.api}users/admin`, { params });
    }

    // Every address of the list's filter (the list is paged), for the "Send email" button: { emails, count }
    getEmails(params: { [key: string]: string }): Observable<any> {
        return this.http.get<any>(`${environment.api}users/admin/emails`, { params });
    }

    getDetail(id): Observable<any> {
        return this.http.get<any[]>(`${environment.api}users/admin/detail/${id}`);
    }

    // The admin's own search: name, username or e-mail, any part, hidden accounts included (the public
    // users/search is the search index, which has neither)
    search(query): Observable<any> {
        return this.http.get<any[]>(`${environment.api}users/admin/search`, { params: { query } });
    }

    create(item): Observable<any> {
        return this.http.put<any[]>(`${environment.api}users/admin`, item);
    }

    update(item): Observable<any> {
        return this.http.post<any[]>(`${environment.api}users/admin`, item);
    }

    delete(userId): Observable<any> {
        return this.http.delete<any>(`${environment.api}users/admin/${userId}`);
    }

    recalculateCategories(userId: string): Observable<any> {
        let params = { userId };
        return this.http.get<any[]>(`${environment.api}users/admin/recalculate-categories`, { params });
    }

    // Admin "log in as user": een sessie voor die gebruiker, geopend op de website.
    impersonate(userId: string): Observable<SessionTokens & { user: any }> {
        return this.http.post<any>(`${environment.api}users/admin/${userId}/impersonate`, {});
    }

    // The engagement scores (Movies-API users/engagement.js): the members with the highest score of a period
    // ('recent' = the last 6 months, 'allTime'), as the API's weekly run stored them, with `computedOn`
    getEngagementRanking(period: 'recent' | 'allTime', limit: number): Observable<any> {
        return this.http.get<any>(`${environment.api}users/admin/engagement`, { params: { period, limit: `${limit}` } });
    }

    // Every member's scores now instead of next Monday, in the background: { started: true }, or
    // { error: true, details: 'alreadyRunning' }
    recalculateEngagement(): Observable<any> {
        return this.http.post<any>(`${environment.api}users/admin/engagement/recalculate`, {});
    }

    logoutUserEverywhere(userId: string): Observable<any> {
        return this.http.post<any>(`${environment.api}users/admin/${userId}/logout-everywhere`, {});
    }

    // Stuurt de bevestigingsmail van deze gebruiker opnieuw. De API antwoordt met
    // { success: true }, { success: true, alreadyConfirmed: true } of
    // { error: true, details: 'tooManyRequests' } -- altijd als HTTP 200.
    resendConfirmationEmail(userId: string): Observable<any> {
        return this.http.post<any>(`${environment.api}users/admin/${userId}/resend-confirmation-email`, {});
    }

    // Auth
    login(body): Observable<any> {
        return Observable.create((observer: Observer<boolean>) => {
            body.isAdmin = true;
            body.client = 'admin';
            this.http.post<any>(`${environment.api}users/auth/login`, body)
                .subscribe(response => {
                    if (response.user && response.token) {
                        this.user = response.user;
                        this.storeTokens(response);
                        observer.next(true);
                    } else {
                        observer.next(false);
                    }
                }, error => {
                    observer.next(false);
                });
        });
    }

    // Enkel een 401/403 van users/auth/current betekent dat het token zelf niet
    // (meer) deugt. Al de rest (geen netwerk, request afgebroken doordat de tab
    // sluit, API even traag/5xx, rate limiter 429) zegt niets over het token.
    // Vroeger gooide elke fout hier de sessie weg -- en omdat de opslag over alle
    // tabs gedeeld wordt, meldde één hikkende achtergrondtab je overal af.
    private isTransient(error: any): boolean {
        if (!(error instanceof HttpErrorResponse)) return true;
        return error.status !== 401 && error.status !== 403;
    }

    validateToken(): Observable<boolean> {
        return Observable.create((observer: Observer<boolean>) => {
            const done = (valid: boolean) => { observer.next(valid); observer.complete(); };

            const token = this.readStoredToken();
            if (!token) return done(false);
            this.token = token;

            // Vervallen (of bijna) access token: eerst vernieuwen, dan pas valideren.
            const fresh = this.accessTokenIsFresh() || !this.refreshToken ? of(true) : this.refreshSession();

            fresh.pipe(switchMap(ok => {
                if (!ok || !this.token) return throwError(new HttpErrorResponse({ status: 401, error: { error: 'refreshFailed' } }));
                const headers = new HttpHeaders({ 'as-admin': 'true', 'x-access-token': this.token });
                return this.rawHttp.post<any>(`${environment.api}users/auth/current`, {}, { headers });
            }))
                .pipe(retryWhen(errors => errors.pipe(concatMap((error, i) => {
                    if (i >= VALIDATE_RETRIES || !this.isTransient(error)) return throwError(error);
                    return timer(VALIDATE_RETRY_DELAY_MS * (i + 1));
                }))))
                .subscribe(response => {
                    this.user = response;
                    this.scheduleProactiveRefresh();
                    done(true);
                }, error => {
                    // Enkel afmelden als de server het token echt afwijst. Bij een
                    // tijdelijke fout blijft het token staan, zodat een herlaadbeurt
                    // je gewoon weer binnenlaat.
                    if (!this.isTransient(error)) this.logout();
                    done(false);
                });
        });
    }

    // Voor de ErrorInterceptor: de API wees een token af op een willekeurige
    // request. Eerst opnieuw valideren; pas als users/auth/current het token ook
    // afwijst, is de sessie echt weg (validateToken logt dan zelf uit).
    revalidateSession(): Observable<boolean> {
        if (!this.revalidation) {
            this.revalidation = this.validateToken().pipe(
                finalize(() => this.revalidation = null),
                share()
            );
        }
        return this.revalidation;
    }

    logout(): void {
        // Best effort: deze sessie ook server-side vergeten, zodat het refresh token
        // dood is, ook als het ooit gelekt is.
        if (this.refreshToken) {
            this.rawHttp.post(`${environment.api}users/auth/logout`, { refreshToken: this.refreshToken })
                .subscribe(() => { }, () => { });
        }
        if (this.refreshTimer) clearTimeout(this.refreshTimer);
        this.refreshTimer = null;
        try {
            localStorage.removeItem(TOKEN_KEY);
            localStorage.removeItem(REFRESH_TOKEN_KEY);
            localStorage.removeItem(EXPIRES_AT_KEY);
        } catch (error) { /* storage geblokkeerd */ }
        this.cookieService.deleteCookie(LEGACY_TOKEN_COOKIE);
        this.token = null;
        this.refreshToken = null;
        this.tokenExpiresAt = null;
        this.user = null;
    }

}
